<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from "vue";

/**
 * Feuille modale glissant depuis le bas sur mobile, centrée sur bureau.
 *
 * S'appuie sur `<dialog showModal()>` : le piégeage du focus, la fermeture par
 * Échap, l'inertie de l'arrière-plan et la restitution du focus à l'élément
 * déclencheur sont assurés nativement par le navigateur. Ne pas réimplémenter.
 */

const props = defineProps<{
  /** Ouverture pilotée par le parent. */
  open: boolean;
  /** Surtitre affiché au-dessus du titre. */
  kicker?: string;
  /** Titre de la feuille, annoncé aux lecteurs d'écran. */
  title: string;
  /** Classes additionnelles appliquées au panneau. */
  panelClass?: string;
  /** Empêche la fermeture par clic extérieur et par Échap. */
  persistent?: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  closed: [];
}>();

const dialog = ref<HTMLDialogElement | null>(null);

const close = () => emit("update:open", false);

watch(
  () => props.open,
  (open) => {
    const node = dialog.value;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  },
  { flush: "post" },
);

/** Échap déclenche `cancel` : on le laisse passer sauf en mode persistant. */
const onCancel = (event: Event) => {
  if (props.persistent) {
    event.preventDefault();
    return;
  }
  close();
};

const onClose = () => {
  emit("closed");
  if (props.open) close();
};

/**
 * Un clic sur l'arrière-plan ferme la feuille, comme sur une feuille native iOS.
 *
 * On teste la cible de l'évènement, et non les coordonnées du pointeur : le
 * fond appartient au `<dialog>` lui-même, donc seul un clic réellement dessus
 * a `target === dialog`. La comparaison par coordonnées, elle, fermait la
 * feuille au moindre clic programmatique — `input.click()` sur le sélecteur de
 * fichiers masqué émet un évènement qui remonte jusqu'ici avec des coordonnées
 * (0, 0), donc considéré à tort comme extérieur au panneau.
 */
const onBackdropClick = (event: MouseEvent) => {
  if (props.persistent) return;
  if (event.target !== dialog.value) return;
  close();
};

onBeforeUnmount(() => {
  if (dialog.value?.open) dialog.value.close();
});
</script>

<template>
  <dialog
    ref="dialog"
    class="sheet"
    :aria-label="title"
    @cancel="onCancel"
    @close="onClose"
    @click="onBackdropClick"
  >
    <div class="sheet__panel" :class="panelClass">
      <div class="sheet__handle" aria-hidden="true" />

      <div class="sheet__heading">
        <div>
          <span v-if="kicker">{{ kicker }}</span>
          <h2>{{ title }}</h2>
        </div>
        <button type="button" class="close-sheet" aria-label="Fermer" @click="close">×</button>
      </div>

      <slot />
    </div>
  </dialog>
</template>

<style scoped>
.sheet {
  width: min(100%, 620px);
  max-width: none;
  max-height: 92svh;
  margin: auto 0 0;
  padding: 0;
  color: var(--color-ink);
  border: 0;
  border-radius: 24px 24px 0 0;
  background: var(--color-ivory);
  box-shadow: var(--shadow-sheet);
  overflow: visible;
}

.sheet::backdrop {
  background: rgb(26 34 29 / 0.66);
  backdrop-filter: blur(6px);
}

.sheet[open] {
  animation: var(--animate-sheet-up);
}

.sheet__panel {
  max-height: 92svh;
  margin: 0;
  padding: 13px 20px 28px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sheet__handle {
  width: 42px;
  height: 4px;
  margin: 0 auto 16px;
  border-radius: 99px;
  background: rgb(38 49 43 / 0.15);
}

.sheet__heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 18px;
}

/* Casse normale : la capitale espacée sonnait administratif au-dessus d'un
   titre qui, lui, appartient à l'album. */
.sheet__heading span {
  color: var(--color-rose-strong);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.sheet__heading h2 {
  margin: 3px 0 0;
  font-family: var(--font-serif);
  font-size: 29px;
  font-weight: 400;
  -webkit-text-stroke: 0.18px currentColor;
}

.close-sheet {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  padding: 0;
  color: var(--color-muted);
  border: 1px solid var(--color-line);
  border-radius: 50%;
  background: var(--color-paper);
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
}

@media (min-width: 760px) {
  .sheet {
    margin: auto;
    border-radius: 24px;
  }

  .sheet__panel {
    padding: 15px 32px 32px;
  }
}
</style>
